import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import {
  Badge,
  Button,
  Card,
  Content as CardContent,
  Text as CardDescription,
  Header as CardHeader,
  Heading as CardTitle,
  CustomDialog,
  DialogContainer,
  TextField as Input,
  LinkButton,
  Picker,
  PickerItem,
} from '@react-spectrum/s2'
import { useState } from 'react'
import {
  ArrowLeft,
  Building,
  Edit,
  Loader2,
  Trash2,
  User,
  UserPlus,
} from 'lucide-react'
import {
  adminAddOrgMember,
  adminRemoveOrgMember,
  getAdminOrganizationDetails,
  updateAdminOrganizationDetails,
} from '@rpc/admin.rpc'
import { fullWidthStyles } from '@/styles/s2-macros'

export const Route = createFileRoute('/admin/organizations/$orgId')({
  loader: async ({ params }) => {
    return {
      data: await getAdminOrganizationDetails({
        data: { orgId: params.orgId },
      }),
    }
  },
  component: OrgDetailsPage,
})

function OrgDetailsPage() {
  const {
    data: { org, members },
  } = Route.useLoaderData()
  const router = useRouter()
  const [isProcessing, setIsProcessing] = useState<string | null>(null)

  // Add member form state
  const [newMemberEmail, setNewMemberEmail] = useState('')
  const [newMemberRole, setNewMemberRole] = useState('member')
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false)

  // Edit org form state
  const [isEditOpen, setIsEditOpen] = useState(false)
  const currentMetadata = parseOrganizationMetadata(org.metadata)
  const [editForm, setEditForm] = useState({
    name: org.name,
    slug: org.slug,
    plan: currentMetadata.plan || 'freeUser',
  })

  const handleSaveOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsProcessing('edit-org')
    try {
      await updateAdminOrganizationDetails({
        data: {
          orgId: org.id,
          data: editForm,
        },
      })
      setIsEditOpen(false)
      router.invalidate()
    } catch (error) {
      alert('Failed to update organization')
    } finally {
      setIsProcessing(null)
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Remove this member from the organization?')) return
    setIsProcessing(memberId)
    try {
      await adminRemoveOrgMember({ data: { memberId } })
      router.invalidate()
    } catch (e) {
      alert('Failed to remove member')
    } finally {
      setIsProcessing(null)
    }
  }

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMemberEmail) return
    setIsProcessing('add-member')
    try {
      await adminAddOrgMember({
        data: {
          orgId: org.id,
          email: newMemberEmail,
          role: newMemberRole,
        },
      })
      setNewMemberEmail('')
      setIsAddMemberOpen(false)
      router.invalidate()
    } catch (error) {
      alert('Failed to add member. Ensure user exists.')
    } finally {
      setIsProcessing(null)
    }
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center gap-4">
        <LinkButton
          href="/admin/organizations"
          variant="secondary"
          fillStyle="outline"
          size="S"
          aria-label="Back to organizations"
        >
          <ArrowLeft className="h-4 w-4" />
        </LinkButton>
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
              {org.name}
            </h2>
            <Badge variant="neutral" fillStyle="outline">
              {org.slug}
            </Badge>
          </div>
          <p className="text-neutral-500 font-mono text-sm">{org.id}</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-1">
          <Card styles={fullWidthStyles}>
            <CardHeader>
              <div className="flex flex-row items-center justify-between pb-2">
                <CardTitle>Details</CardTitle>
                <Button
                  variant="secondary"
                  fillStyle="outline"
                  size="S"
                  aria-label="Edit organization"
                  onPress={() => setIsEditOpen(true)}
                >
                  <Edit className="h-4 w-4 text-neutral-500" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 pt-6">
                <div className="mb-4 flex justify-center">
                  <div className="h-24 w-24 rounded bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-500">
                    {org.logo ? (
                      <img
                        src={org.logo}
                        alt={`${org.name} logo`}
                        className="h-24 w-24 object-cover rounded"
                      />
                    ) : (
                      <Building className="h-10 w-10" />
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase text-neutral-500">
                    Plan
                  </label>
                  <p>{formatOrganizationPlan(org.metadata)}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase text-neutral-500">
                    Created
                  </label>
                  <p>{new Date(org.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase text-neutral-500">
                    Total Members
                  </label>
                  <p>{members.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="md:col-span-2">
          <Card styles={fullWidthStyles}>
            <CardHeader>
              <div className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Members</CardTitle>
                  <CardDescription>
                    Manage access to this organization.
                  </CardDescription>
                </div>
                <Button size="S" onPress={() => setIsAddMemberOpen(true)}>
                  <UserPlus className="h-4 w-4" />
                  Add member
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {members.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between rounded-lg border border-neutral-200/80 p-3 transition-colors hover:bg-neutral-50/70 dark:border-neutral-800/80 dark:hover:bg-neutral-900/40"
                  >
                    <div className="flex items-center gap-3">
                      <Link
                        to="/admin/users/$userId"
                        params={{ userId: m.userId }}
                      >
                        <div className="h-8 w-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-500 hover:bg-blue-100 hover:text-blue-600 transition-colors">
                          <User className="h-4 w-4" />
                        </div>
                      </Link>
                      <div>
                        <p className="font-medium">{m.name}</p>
                        <p className="text-xs text-neutral-500">{m.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-16">
                        <Badge variant="neutral" fillStyle="outline">
                          {capitalize(m.role)}
                        </Badge>
                      </div>
                      <Button
                        variant="negative"
                        fillStyle="outline"
                        size="S"
                        aria-label={`Remove ${m.name} from organization`}
                        onPress={() => handleRemoveMember(m.id)}
                        isDisabled={isProcessing === m.id}
                      >
                        {isProcessing === m.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {isEditOpen ? (
        <DialogContainer onDismiss={() => setIsEditOpen(false)}>
          <CustomDialog isDismissible aria-label="Edit organization">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Edit organization</h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                Update organization details and plan.
              </p>
              <form onSubmit={handleSaveOrg} className="space-y-4 py-2">
                <div className="grid gap-2">
                  <label>Name</label>
                  <Input
                    aria-label="Organization name"
                    value={editForm.name}
                    onChange={(value) =>
                      setEditForm({ ...editForm, name: value })
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <label>Slug (URL Identifier)</label>
                  <Input
                    aria-label="Organization slug"
                    value={editForm.slug}
                    onChange={(value) =>
                      setEditForm({ ...editForm, slug: value })
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <label>Plan tier</label>
                  <Picker
                    aria-label="Plan tier"
                    placeholder="Select plan tier"
                    selectedKey={editForm.plan}
                    onSelectionChange={(key) =>
                      setEditForm({ ...editForm, plan: String(key) })
                    }
                  >
                    <PickerItem id="freeUser">Free</PickerItem>
                    <PickerItem id="registeredUser">Registered</PickerItem>
                    <PickerItem id="premiumUser">Premium</PickerItem>
                    <PickerItem id="proUser">Pro</PickerItem>
                    <PickerItem id="enterpriseUser">Enterprise</PickerItem>
                  </Picker>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    isDisabled={isProcessing === 'edit-org'}
                  >
                    {isProcessing === 'edit-org' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Save changes'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </CustomDialog>
        </DialogContainer>
      ) : null}

      {isAddMemberOpen ? (
        <DialogContainer onDismiss={() => setIsAddMemberOpen(false)}>
          <CustomDialog isDismissible aria-label="Add member">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Add member</h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                Force-add an existing user to this organization.
              </p>
              <form onSubmit={handleAddMember} className="space-y-4 py-2">
                <div className="grid gap-2">
                  <label>User email</label>
                  <Input
                    aria-label="Member email"
                    placeholder="user@example.com"
                    value={newMemberEmail}
                    onChange={setNewMemberEmail}
                    isRequired
                  />
                </div>
                <div className="grid gap-2">
                  <label>Role</label>
                  <Picker
                    aria-label="Role"
                    placeholder="Select role"
                    selectedKey={newMemberRole}
                    onSelectionChange={(key) => setNewMemberRole(String(key))}
                  >
                    <PickerItem id="member">Member</PickerItem>
                    <PickerItem id="admin">Admin</PickerItem>
                    <PickerItem id="owner">Owner</PickerItem>
                  </Picker>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    isDisabled={isProcessing === 'add-member'}
                  >
                    {isProcessing === 'add-member' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Add user'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </CustomDialog>
        </DialogContainer>
      ) : null}
    </div>
  )
}

function capitalize(value: string) {
  if (!value) return value
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function formatOrganizationPlan(metadata?: string | null) {
  const parsed = parseOrganizationMetadata(metadata)
  const plan = parsed.plan || 'Free'
  return capitalize(String(plan))
}

function parseOrganizationMetadata(metadata?: string | null) {
  if (!metadata) return {}
  try {
    const parsed = JSON.parse(metadata)
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}
